import { Component, EventEmitter, Input, Output } from "@angular/core";
import dayjs from "dayjs";
import {
  CampaignPostContentType,
  LinkedInPostContentType,
} from "../../../../shared/enums/campaignPost.enums";
import { AppLocale } from "../../../../shared/models/appData";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { BrandCampaignFacebookPost } from "../../../../shared/models/brandCampaignFacebookPost";
import { BrandCampaignGoogleAdPost } from "../../../../shared/models/brandCampaignGoogleAdPost";
import { BrandCampaignLinkedInPost } from "../../../../shared/models/brandCampaignLinkedInPost";
import { BrandPost } from "../../../../shared/models/brandCampaignPost";
import { DialogService } from "../../../../shared/services/dialog.service";
import { ImageService } from "../../../../shared/services/image.service";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignDialogService } from "../../brand-campaign-dialog.service";
import { BrandCampaignPostService } from "../../shared/services/brand-campaign-post.service";
import { LanguageService } from "../../../../shared/services/language.service";

interface IApprovalUser {
  user: string;
  date: string;
}

@Component({
  selector: "app-campaign-content-post",
  templateUrl: "./campaign-content-post.component.html",
  styleUrls: ["./campaign-content-post.component.scss"],
})
export class CampaignContentPostComponent {
  @Input({ required: true }) post!: BrandPost;
  @Input({ required: true }) campaign!: BrandCampaign;
  @Input({ required: true }) campaignDetails!: BrandCampaignDetails;
  @Input({ required: true }) category!: BrandCampaignCategory;
  @Input({ required: true }) isEditable!: boolean;
  @Output() postDeleted = new EventEmitter<number>();

  public readonly CampaignPostContentType = CampaignPostContentType;
  public readonly LinkedInContentType = LinkedInPostContentType;
  public saving = false;

  constructor(
    private readonly brandCampaignDialogService: BrandCampaignDialogService,
    private readonly brandCampaignPostService: BrandCampaignPostService,
    private readonly dialogService: DialogService,
    private readonly imageService: ImageService,
    private readonly languageService: LanguageService,
    private readonly notificationService: NotificationService,
  ) {}

  public onPreviewPost(): void {
    this.dialogService.showPostPreview({
      post: this.post,
    });
  }

  public async onEditPost(): Promise<void> {
    const dialogService = this.brandCampaignDialogService;
    const basicData = {
      campaign: this.campaign,
      campaignDetails: this.campaignDetails,
      category: this.category,
    };

    if (this.post.isFacebookPost) {
      const data = {
        ...basicData,
        post: this.post as BrandCampaignFacebookPost,
      };

      switch (this.post.mediaType) {
        case CampaignPostContentType.Carousel:
          return await dialogService.showCarouselPostForm(data);
        case CampaignPostContentType.Video:
          return await dialogService.showVideoPostForm(data);
        case CampaignPostContentType.Image:
        case CampaignPostContentType.Image360:
        case CampaignPostContentType.Gif:
          return await dialogService.showImagePostForm({
            ...data,
            postContentType: this.post.mediaType,
          });
        case CampaignPostContentType.LinkPost:
          return await dialogService.showLinkPostForm(data);
      }
    } else if (this.post.isGoogleAd) {
      return await dialogService.showGoogleAdsPostForm({
        ...basicData,
        googleAdPost: this.post as BrandCampaignGoogleAdPost,
      });
    } else if (this.post.isLinkedInPost) {
      const data = {
        ...basicData,
        contentType: this.post.mediaType as LinkedInPostContentType,
        post: this.post as BrandCampaignLinkedInPost,
      };

      switch (this.post.mediaType) {
        case LinkedInPostContentType.Image:
          return await dialogService.showLinkedInImagePostForm(data);
        case LinkedInPostContentType.MultiImage:
          return await dialogService.showLinkedInMultiImagePostForm(data);
        case LinkedInPostContentType.Video:
          return await dialogService.showLinkedInVideoPostForm(data);
      }
    }
  }

  public onDeletePostConfirm(): void {
    this.dialogService.confirm({
      title: "brand.campaign.content.deletePostConfirmTitle",
      message: "brand.campaign.content.deletePostConfirm",
      onConfirm: () => this.onDeletePost(),
    });
  }

  public onDeletePost(): void {
    this.brandCampaignPostService
      .deleteCampaignDetailCategoryPost(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.post.id,
      )
      .subscribe({
        next: () => {
          this.postDeleted.emit(this.post.id);
        },
        error: () => {
          this.notificationService.error("shared.errorSavingChanges");
        },
      });
  }

  public resizeImage(url: string, imagePublicId: string): string {
    return this.imageService.resizeImage(url, 1000, imagePublicId);
  }

  public onApprovePost(): void {
    if (this.isPendingReview()) {
      this.dialogService.confirm({
        title: "brand.campaign.content.approval.confirmationTitle",
        message: "brand.campaign.content.approval.confirmation",
        confirmButton: "brand.campaign.content.approval.resolveComments",
        onConfirm: () => this.approvePost(),
      });
    } else {
      this.approvePost();
    }
  }

  public approvePost(): void {
    this.saving = true;
    this.brandCampaignPostService
      .updateCampaignDetailCategoryPostReview(
        this.post.campaignId,
        this.campaignDetails.id,
        this.category.id,
        this.post.id,
        {
          reviewImageComment: this.post.reviewImageComment,
          isReviewImageCommentResolved: this.post.isReviewImageCommentResolved,
          reviewTextComment: this.post.reviewTextComment,
          isReviewTextCommentResolved: this.post.isReviewTextCommentResolved,
          isReviewApproved: true,
        },
      )
      .subscribe({
        next: (post: BrandPost) => {
          this.post = post;
          this.notificationService.success("shared.approved");
          this.saving = false;
        },
        error: () => {
          this.saving = false;
        },
      });
  }

  public onDisapprovePost(): void {
    this.saving = true;
    this.brandCampaignPostService
      .updateCampaignDetailCategoryPostReview(
        this.post.campaignId,
        this.campaignDetails.id,
        this.category.id,
        this.post.id,
        {
          reviewImageComment: this.post.reviewImageComment,
          isReviewImageCommentResolved: this.post.isReviewImageCommentResolved,
          reviewTextComment: this.post.reviewTextComment,
          isReviewTextCommentResolved: this.post.isReviewTextCommentResolved,
          isReviewApproved: false,
        },
      )
      .subscribe({
        next: (post: BrandPost) => {
          this.post = post;
          this.saving = false;
        },
        error: () => {
          this.saving = false;
        },
      });
  }

  public async onCommentPost(): Promise<void> {
    const post = await this.brandCampaignDialogService.showContentPostComments({
      post: this.post,
      campaignDetailsId: this.campaignDetails.id,
      categoryId: this.category.id,
    });

    if (post) {
      this.post = post;
    }
  }

  public isPendingReview(): boolean {
    if (this.post.isReviewApproved) {
      return false;
    }

    const isTextReviewPending =
      !!this.post.reviewTextComment && !this.post.isReviewTextCommentResolved;

    const isImageReviewPending =
      !!this.post.reviewImageComment && !this.post.isReviewImageCommentResolved;

    return isTextReviewPending || isImageReviewPending;
  }

  public getApprovalUserData(): IApprovalUser {
    return {
      user: this.post.reviewApprovedBy,
      date: dayjs(this.post.reviewApprovedAt).format("MMMM Do YYYY, h:mm:ss a"),
    };
  }

  public onMoveCategoryConfirm(): void {
    this.brandCampaignDialogService.showBrandCampaignCategoryMove({
      campaign: this.campaign,
      category: this.category,
      post: this.post,
    });
  }

  public getEmptyPartnerLogo(): string {
    return this.languageService.locale === AppLocale.DeDe
      ? "/assets/images/google-ads-retailer-logo.png" // TODO add german image
      : "/assets/images/google-ads-retailer-logo.png";
  }
}
